import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import { LuFileText, LuFileUp, LuX } from 'react-icons/lu';
import { useTranslation } from '../../../i18n/LanguageContext';
import { pickPdfFiles } from './expenseOptions';

/**
 * Picks one or more PDFs (10 MB each at most) and lists them with a remove
 * button. `files` / `onChange` hold the selection.
 */
const PdfPicker = ({ id, files, onChange, disabled }) => {
  const { t } = useTranslation();
  const label = (key, vars) => t(`profilePage.expense.pdf.${key}`, vars);
  const inputRef = useRef(null);

  const handleSelect = (event) => {
    const valid = pickPdfFiles(event.target.files, (reason, file) => toast.error(label(reason, { name: file.name })));
    if (valid.length > 0) {
      onChange([...files, ...valid]);
      toast.success(label('selected', { count: valid.length }));
    }
    // Aynı dosya yeniden seçilebilsin
    event.target.value = '';
  };

  return (
    <div className="tw-space-y-2">
      <input ref={inputRef} id={id} type="file" accept="application/pdf,.pdf" multiple onChange={handleSelect} disabled={disabled} className="tw-hidden" />
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        disabled={disabled}
        className="tw-flex tw-w-full tw-flex-col tw-items-center tw-gap-1.5 tw-rounded-2xl tw-border-2 tw-border-dashed tw-border-slate-300 tw-bg-slate-50/60 tw-px-4 tw-py-5 tw-text-center tw-transition-colors hover:tw-border-teal-400 hover:tw-bg-teal-500/[0.04] focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-teal-500/50 disabled:tw-cursor-not-allowed disabled:tw-opacity-60 dark:tw-border-white/15 dark:tw-bg-white/[0.02] dark:hover:tw-border-teal-400/60"
      >
        <LuFileUp aria-hidden="true" className="tw-h-6 tw-w-6 tw-text-teal-600 dark:tw-text-teal-300" />
        <span className="tw-text-sm tw-font-semibold tw-text-slate-700 dark:tw-text-slate-200">{label('choose')}</span>
        <span className="tw-text-xs tw-text-slate-500 dark:tw-text-slate-400">{label('limits')}</span>
      </button>

      {files.length > 0 && (
        <div className="tw-space-y-1.5">
          <div className="tw-flex tw-items-center tw-justify-between tw-gap-2 tw-px-0.5">
            <span className="tw-text-xs tw-font-semibold tw-text-slate-600 dark:tw-text-slate-300">{label('selectedTitle', { count: files.length })}</span>
            <button
              type="button"
              onClick={() => {
                onChange([]);
                toast.success(label('allRemoved'));
              }}
              className="tw-rounded-full tw-bg-transparent tw-px-2 tw-py-1 tw-text-xs tw-font-semibold tw-text-rose-600 hover:tw-bg-rose-500/10 dark:tw-text-rose-300"
            >
              {label('removeAll')}
            </button>
          </div>
          <ul className="tw-space-y-1.5">
            {files.map((file, index) => (
              // eslint-disable-next-line react/no-array-index-key
              <li key={`${file.name}-${index}`} className="tw-flex tw-items-center tw-gap-2.5 tw-rounded-xl tw-border tw-border-slate-200 tw-bg-white/70 tw-px-3 tw-py-2 dark:tw-border-white/10 dark:tw-bg-white/[0.04]">
                <LuFileText aria-hidden="true" className="tw-h-4 tw-w-4 tw-shrink-0 tw-text-rose-500" />
                <span className="tw-min-w-0 tw-flex-1">
                  <span className="tw-block tw-truncate tw-text-sm tw-font-medium tw-text-slate-800 dark:tw-text-slate-100">{file.name}</span>
                  <span className="tw-block tw-text-[11px] tw-tabular-nums tw-text-slate-500 dark:tw-text-slate-400">{(file.size / 1024 / 1024).toFixed(2)} MB</span>
                </span>
                <button
                  type="button"
                  onClick={() => {
                    onChange(files.filter((_, i) => i !== index));
                    toast.success(label('removed'));
                  }}
                  aria-label={label('removeOne', { name: file.name })}
                  className="tw-grid tw-h-7 tw-w-7 tw-shrink-0 tw-place-items-center tw-rounded-full tw-bg-transparent tw-text-slate-400 hover:tw-bg-rose-500/10 hover:tw-text-rose-600 dark:hover:tw-text-rose-300"
                >
                  <LuX aria-hidden="true" className="tw-h-4 tw-w-4" />
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

PdfPicker.propTypes = {
  id: PropTypes.string.isRequired,
  files: PropTypes.arrayOf(PropTypes.object).isRequired,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};

export default PdfPicker;
